import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRequest } from "../apiRequests/requestApi";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function MyLobbies() {
    const [lobbies, setLobbies] = useState([]);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const lobbiesPerPage = 6;

    useEffect(() => {
        async function fetchLobbiesAndGames() {
            setLoading(true);
            setError(null);
            try {
                const [lobbiesRes, gamesRes] = await Promise.all([
                    getRequest(`/mylobbies`),
                    getRequest(`/games`)
                ]);
                let lobbiesList = [];
                if (Array.isArray(lobbiesRes)) {
                    lobbiesList = lobbiesRes;
                } else if (lobbiesRes && lobbiesRes.data) {
                    lobbiesList = lobbiesRes.data;
                }
                let gamesList = [];
                if (Array.isArray(gamesRes)) {
                    gamesList = gamesRes;
                } else if (gamesRes && gamesRes.data) {
                    gamesList = gamesRes.data;
                }
                setLobbies(lobbiesList);
                setGames(gamesList);
            } catch (err) {
                setError(err?.message || "Failed to fetch lobbies");
                setLobbies([]);
                setGames([]);
            } finally {
                setLoading(false);
            }
        }
        fetchLobbiesAndGames();
    }, []);

    // Get current user ID from localStorage/sessionStorage
    let currentUserId = null;
    try {
        const user = JSON.parse(localStorage.getItem('user')) || JSON.parse(sessionStorage.getItem('user'));
        currentUserId = user?._id;
    } catch (e) {}

    const getGameInfo = (gameId) => {
        return games.find((g) => g._id === gameId) || {};
    };

    const handleRejoin = (lobby) => {
        navigate(
            `/games/selected-user/${lobby.gameId}/${lobby.creatorId}/waitingForOpponent/${lobby._id}/${lobby.code}/${getGameInfo(lobby.gameId).name || lobby.gameName || "Game"}`
        );
    };

    // Only show lobbies created within the last hour
    const now = Date.now();
    const lobbiesWithinHour = lobbies.filter(lobby => {
        const created = new Date(lobby.createdAt).getTime();
        return (now - created) <= 60 * 60 * 1000;
    });
    const totalPages = Math.ceil(lobbiesWithinHour.length / lobbiesPerPage);
    const paginatedLobbies = lobbiesWithinHour.slice((currentPage - 1) * lobbiesPerPage, currentPage * lobbiesPerPage);
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#130828] text-white p-4 md:p-8">
            <button
                className="mb-6 text-white bg-[#2E1243]  px-5 py-2 rounded-full font-bold shadow-lg transition-all duration-150"
                onClick={() => {
                    if (window.history.length > 2) {
                        navigate(-1);
                    } else {
                        navigate("/");
                    }
                }}
            >
                &larr; Back
            </button>
            <h1 className="text-3xl font-extrabold mb-8 tracking-tight text-center md:text-left">My Active Lobbies</h1>
            {loading ? (
                <div className="text-center text-lg font-semibold">Loading...</div>
            ) : error ? (
                <div className="text-red-400 text-center font-semibold">{error}</div>
            ) : lobbiesWithinHour.length === 0 ? (
                <div className="text-center text-gray-400">No active lobbies found.</div>
            ) : (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedLobbies.map((lobby) => {
                        const gameInfo = getGameInfo(lobby.gameId);
                        const isCreator = lobby.creatorId === currentUserId;
                        return (
                            <div
                                key={lobby._id}
                                className="bg-[#1F0A35] rounded-xl p-3 flex flex-col gap-2 shadow-md hover:shadow-xl border border-[#2E1243] transition-all duration-150 min-h-[170px]"
                            >
                                <div className="flex items-center gap-3 mb-1">
                                    {gameInfo.image && (
                                        <img src={gameInfo.image} alt={gameInfo.name} className="w-10 h-10 rounded object-cover border-2 border-[#3F9217]" />
                                    )}
                                    <div>
                                        <div className="font-bold text-lg text-[#D88BEE]">
                                            {gameInfo.name || lobby.gameName || "Game"}
                                        </div>
                                        {isCreator && (
                                            <div className="text-xs text-green-400 font-bold mt-1">You created this lobby</div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                    <div className="text-gray-300">Lobby Code: <span className="font-mono text-white">{lobby.code}</span></div>
                                    <div className="text-gray-400">Created: {new Date(lobby.createdAt).toLocaleString()}</div>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                    <div className="text-gray-400">Wager: <span className="font-mono text-[#D88BEE]">{lobby.wagerAmount/100}</span></div>
                                    <div className="text-gray-400">Players: <span className="font-mono text-white">{lobby.participants?.length || 0}</span></div>
                                    <div className="text-gray-400">Games: <span className="font-mono text-white">{lobby.noOfGamesPlayed}</span></div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
                                    <span>Status:</span>
                                    <span className={lobby.active ? "text-green-400 font-bold" : "text-red-400 font-bold"}>
                                        {lobby.active ? "Active" : "Inactive"}
                                    </span>
                                    {/* {lobby.dead && (
                                        <span className="inline-block rounded-full bg-red-600/90 text-white px-3 py-1 text-xs font-bold ml-1 shadow-sm border border-red-300">Game Ended</span>
                                    )} */}
                                    {/* {lobby.inGame && !lobby.dead && (
                                        <span className="inline-block rounded-full bg-yellow-400/90 text-black px-3 py-1 text-xs font-bold ml-1 shadow-sm border border-yellow-300">In Game</span>
                                    )} */}
                                </div>
                                <button
                                    className={`mt-3 px-4 py-2 rounded-full font-bold text-base transition-all duration-150 shadow-md self-end ${lobby.dead ? 'bg-gray-500 cursor-not-allowed opacity-60' : 'bg-[#56920D] hover:bg-[#3F9217] text-white'}`}
                                    onClick={() => !lobby.dead && handleRejoin(lobby)}
                                    disabled={lobby.dead}
                                    title={lobby.dead ? 'Game Ended' : 'Rejoin'}
                                >
                                    {lobby.dead ? 'Game Ended' : 'Rejoin'}
                                </button>
                                
                            </div>
                        );
                    })}
                </div>
                {/* Pagination Controls */}
                <div className="flex justify-center items-center gap-4 mt-8">
                  <button
                    className="px-4 py-2 rounded-full bg-[#2E1243] text-white font-bold shadow disabled:opacity-50 transition-all duration-150"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="text-white font-semibold">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="px-4 py-2 rounded-full bg-[#2E1243] text-white font-bold shadow disabled:opacity-50 transition-all duration-150"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
                </>
            )}
        </div>
    );
}
